import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { parseDRFError } from '../../utils/apiUtils';

// Default options for quick setup
const DEFAULT_OPTIONS = {
  referralTypes: [
    { name: 'Cardiology Clearance', slug: 'cardiology', description: 'Cardiac evaluation and clearance', icon: 'heartbeat', order: 0, is_active: true },
    { name: 'Pulmonology Clearance', slug: 'pulmonology', description: 'Respiratory evaluation and clearance', icon: 'lungs', order: 1, is_active: true },
    { name: 'Neurology Clearance', slug: 'neurology', description: 'Neurological evaluation', icon: 'brain', order: 2, is_active: true },
    { name: 'Psychiatry Clearance', slug: 'psychiatry', description: 'Psychiatric evaluation', icon: 'user-md', order: 3, is_active: true },
    { name: 'Dental Clearance', slug: 'dental', description: 'Dental evaluation and clearance', icon: 'tooth', order: 4, is_active: true },
    { name: 'Lab Work', slug: 'lab-work', description: 'Laboratory tests and results', icon: 'flask', order: 5, is_active: true },
    { name: 'Radiology', slug: 'radiology', description: 'Imaging studies (X-ray, CT, MRI)', icon: 'x-ray', order: 6, is_active: true },
    { name: 'Physical Therapy', slug: 'physical-therapy', description: 'PT evaluation and clearance', icon: 'dumbbell', order: 7, is_active: true },
    { name: 'Anesthesia Clearance', slug: 'anesthesia', description: 'Anesthesia evaluation', icon: 'syringe', order: 8, is_active: true },
    { name: 'Insurance Authorization', slug: 'insurance-auth', description: 'Insurance pre-authorization', icon: 'file-medical', order: 9, is_active: true },
    { name: 'Vendor Onboarding', slug: 'vendor-onboarding', description: 'Vendor credential verification', icon: 'building', order: 10, is_active: true },
    { name: 'Background Check', slug: 'background-check', description: 'Background verification', icon: 'user-shield', order: 11, is_active: true },
  ],
  referralStatuses: [
    { name: 'Not Started', slug: 'not-started', description: 'Referral not yet initiated', color: '#6c757d', is_default: true, is_complete: false, order: 0, is_active: true },
    { name: 'Pending', slug: 'pending', description: 'Awaiting response or action', color: '#ffc107', is_complete: false, order: 1, is_active: true },
    { name: 'In Progress', slug: 'in-progress', description: 'Currently being processed', color: '#17a2b8', is_complete: false, order: 2, is_active: true },
    { name: 'Awaiting Documentation', slug: 'awaiting-docs', description: 'Waiting for required documents', color: '#fd7e14', is_complete: false, order: 3, is_active: true },
    { name: 'Under Review', slug: 'under-review', description: 'Being reviewed by specialist', color: '#6f42c1', is_complete: false, order: 4, is_active: true },
    { name: 'Approved', slug: 'approved', description: 'Clearance approved', color: '#28a745', is_complete: true, order: 5, is_active: true },
    { name: 'Completed', slug: 'completed', description: 'Fully completed', color: '#28a745', is_complete: true, order: 6, is_active: true },
    { name: 'Declined', slug: 'declined', description: 'Clearance declined', color: '#dc3545', is_complete: false, order: 7, is_active: true },
    { name: 'Cancelled', slug: 'cancelled', description: 'Referral cancelled', color: '#6c757d', is_complete: false, order: 8, is_active: true },
    { name: 'On Hold', slug: 'on-hold', description: 'Temporarily paused', color: '#e83e8c', is_complete: false, order: 9, is_active: true },
  ],
};

const ReferralManagementSettings = () => {
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Organization/Health System scoping - separated lists
  const [organizations, setOrganizations] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);

  // Top-level filter: which entity type to filter by
  const [filterType, setFilterType] = useState('global'); // 'global', 'health_system', 'organization'
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedHealthSystemId, setSelectedHealthSystemId] = useState('');

  // Form scoping - for create/edit forms
  const [formAssignmentType, setFormAssignmentType] = useState('global');

  // Data states
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Fetch data when tab or filter changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterType, selectedOrgId, selectedHealthSystemId]);

  const fetchOrganizations = async () => {
    try {
      // Fetch both sales organizations and health systems separately
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        adminApi.get('admin/sales/organizations/', { params: { is_active: 'true' } }),
        adminApi.get('admin/health-systems/')
      ]);

      // Handle sales organizations response
      if (results[0].status === 'fulfilled') {
        setOrganizations(results[0].value.data?.organizations || []);
      } else {
        console.warn('Failed to fetch sales organizations:', results[0].reason?.message);
        setOrganizations([]);
      }

      // Handle health systems response
      if (results[1].status === 'fulfilled') {
        setHealthSystems(results[1].value.data?.health_systems || []);
      } else {
        console.error('Failed to fetch health systems:', results[1].reason?.message);
        setHealthSystems([]);
      }
    } catch (err) {
      console.error('Unexpected error in fetchOrganizations:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query params based on filterType
      const params = {};
      if (filterType === 'health_system' && selectedHealthSystemId) {
        params.health_system_id = selectedHealthSystemId;
      } else if (filterType === 'organization' && selectedOrgId) {
        params.organization_id = selectedOrgId;
      }
      // For filterType === 'global', send no params to get ALL items
      // (we'll filter client-side to show global vs scoped)

      switch (activeTab) {
        case 'types': {
          const typesRes = await adminApi.get('admin/referral-types/', { params });
          setTypes(typesRes.data?.referral_types || []);
          break;
        }
        case 'statuses': {
          const statusesRes = await adminApi.get('admin/referral-statuses/', { params });
          setStatuses(statusesRes.data?.referral_statuses || []);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  // Handler for top-level filter type change
  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    if (type === 'health_system') {
      setSelectedOrgId('');
    } else if (type === 'organization') {
      setSelectedHealthSystemId('');
    } else {
      setSelectedOrgId('');
      setSelectedHealthSystemId('');
    }
  };

  // Handler for form assignment type change
  const handleFormAssignmentTypeChange = (type) => {
    setFormAssignmentType(type);
    if (type === 'health_system') {
      setFormData(prev => ({ ...prev, organization_id: null }));
    } else if (type === 'organization') {
      setFormData(prev => ({ ...prev, health_system_id: null }));
    } else {
      setFormData(prev => ({ ...prev, health_system_id: null, organization_id: null }));
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setFormAssignmentType(filterType); // Default to current filter type
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);

    // Extract IDs from nested objects for the form
    const formDataWithIds = {
      ...item,
      health_system_id: item.health_system?.id || null,
      organization_id: item.organization?.id || null
    };
    setFormData(formDataWithIds);

    // Determine assignment type from item
    const hasHealthSystem = item.health_system;
    const hasOrganization = item.organization;
    setFormAssignmentType(hasHealthSystem ? 'health_system' : hasOrganization ? 'organization' : 'global');

    setShowForm(true);
  };

  const handleSetDefault = async (item) => {
    try {
      const endpoint = getEndpoint();
      await adminApi.patch(`${endpoint}${item.id}/`, { is_default: true });
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      const endpoint = getEndpoint();
      await adminApi.delete(`${endpoint}${item.id}/`);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = getEndpoint();

      // Exclude nested objects and read-only fields to prevent DRF serializer conflicts
      const { organization, health_system, organization_name, health_system_name, created_at, updated_at, ...cleanData } = formData;

      // Build payload based on formAssignmentType
      const payload = {
        ...cleanData,
        health_system_id: formAssignmentType === 'health_system' ? formData.health_system_id : null,
        organization_id: formAssignmentType === 'organization' ? formData.organization_id : null,
      };

      if (editingItem) {
        // Update existing item
        await adminApi.patch(`${endpoint}${editingItem.id}/`, payload);
      } else {
        // Create new item
        await adminApi.post(endpoint, payload);
      }

      setShowForm(false);
      setFormData({});
      setEditingItem(null);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    const scopeMsg = filterType === 'global' ? 'globally' :
                     filterType === 'health_system' ? 'for this health system' :
                     'for this organization';
    if (!window.confirm(`Create all default options ${scopeMsg}? This will add multiple items.`)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const endpoint = getEndpoint();
      const defaults = activeTab === 'types' ? DEFAULT_OPTIONS.referralTypes : DEFAULT_OPTIONS.referralStatuses;

      // Create all defaults sequentially with scoping
      for (const defaultItem of defaults) {
        let itemData = { ...defaultItem };

        // Add scoping based on filterType
        if (filterType === 'health_system' && selectedHealthSystemId) {
          itemData.health_system_id = selectedHealthSystemId;
        } else if (filterType === 'organization' && selectedOrgId) {
          itemData.organization_id = selectedOrgId;
        }

        await adminApi.post(endpoint, itemData);
      }

      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSingleDefault = async (defaultItem) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = getEndpoint();
      let itemData = { ...defaultItem };

      // Add scoping based on filterType
      if (filterType === 'health_system' && selectedHealthSystemId) {
        itemData.health_system_id = selectedHealthSystemId;
      } else if (filterType === 'organization' && selectedOrgId) {
        itemData.organization_id = selectedOrgId;
      }

      await adminApi.post(endpoint, itemData);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = () => {
    switch (activeTab) {
      case 'types':
        return 'admin/referral-types/';
      case 'statuses':
        return 'admin/referral-statuses/';
      default:
        return '';
    }
  };

  const getDefaultFormData = () => {
    const base = {
      name: '',
      slug: '',
      description: '',
      is_active: true,
      health_system_id: filterType === 'health_system' ? selectedHealthSystemId : null,
      organization_id: filterType === 'organization' ? selectedOrgId : null,
    };

    switch (activeTab) {
      case 'types':
        return { ...base, icon: '', order: 0 };
      case 'statuses':
        return { ...base, color: '#808080', is_default: false, order: 0 };
      default:
        return base;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'types':
        return types;
      case 'statuses':
        return statuses;
      default:
        return [];
    }
  };

  const renderDefaultOptions = () => {
    const defaults = activeTab === 'types' ? DEFAULT_OPTIONS.referralTypes : DEFAULT_OPTIONS.referralStatuses;
    const tabTitle = activeTab === 'types' ? 'Referral Types' : 'Referral Statuses';

    return (
      <div className="p-4">
        <div className="alert alert-info mb-4">
          <i className="fas fa-lightbulb me-2"></i>
          <strong>Quick Setup:</strong> No {tabTitle.toLowerCase()} found. Create default options to get started.
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Suggested Defaults</h6>
          <button
            className="btn btn-success btn-sm"
            onClick={handleCreateDefaults}
            disabled={loading}
          >
            <i className="fas fa-magic me-2"></i>
            Create All Defaults
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Order</th>
                {activeTab === 'types' && <th>Icon</th>}
                {activeTab === 'statuses' && <th>Color</th>}
                {activeTab === 'statuses' && <th>Default</th>}
                {activeTab === 'statuses' && <th>Complete</th>}
                <th style={{ width: '100px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {defaults.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td><code className="small">{item.slug}</code></td>
                  <td>{item.order}</td>
                  {activeTab === 'types' && (
                    <td>
                      {item.icon && <i className={`fas fa-${item.icon} me-2`}></i>}
                      {item.icon}
                    </td>
                  )}
                  {activeTab === 'statuses' && (
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: item.color, color: '#fff' }}
                      >
                        {item.color}
                      </span>
                    </td>
                  )}
                  {activeTab === 'statuses' && (
                    <td>
                      {item.is_default && <i className="fas fa-check text-success"></i>}
                    </td>
                  )}
                  {activeTab === 'statuses' && (
                    <td>
                      {item.is_complete && <i className="fas fa-check-circle text-success"></i>}
                    </td>
                  )}
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleCreateSingleDefault(item)}
                      disabled={loading}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Create
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const data = getCurrentData();

    if (loading) {
      return (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    // Show prompt if filter requires selection but none is made
    if (filterType === 'health_system' && !selectedHealthSystemId) {
      return (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Please select a <strong>Health System</strong> from the dropdown above to view and manage its specific referral {activeTab}.
        </div>
      );
    }

    if (filterType === 'organization' && !selectedOrgId) {
      return (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Please select an <strong>Organization</strong> from the dropdown above to view and manage its specific referral {activeTab}.
        </div>
      );
    }

    if (data.length === 0) {
      return renderDefaultOptions();
    }

    // Split data into scoped vs global items when filtering by health_system or organization
    const scopedItems = [];
    const globalItems = [];

    if (filterType === 'health_system' && selectedHealthSystemId) {
      data.forEach(item => {
        if (item.health_system?.id === selectedHealthSystemId) {
          scopedItems.push(item);
        } else if (!item.health_system && !item.organization) {
          globalItems.push(item);
        }
      });
    } else if (filterType === 'organization' && selectedOrgId) {
      data.forEach(item => {
        if (item.organization?.id === selectedOrgId) {
          scopedItems.push(item);
        } else if (!item.health_system && !item.organization) {
          globalItems.push(item);
        }
      });
    } else {
      // For global filter, just show global items
      data.forEach(item => {
        if (!item.health_system && !item.organization) {
          globalItems.push(item);
        }
      });
    }

    const renderItemRows = (items) => items.map((item) => (
      <tr key={item.id}>
        <td>
          {item.name}
          <br />
          <small className="text-muted">{item.description}</small>
        </td>
        <td><code>{item.slug}</code></td>
        <td>
          {item.health_system ? (
            <span className="badge bg-primary">
              <i className="fas fa-hospital me-1"></i>
              {item.health_system.name || 'HS-Specific'}
            </span>
          ) : item.organization ? (
            <span className="badge bg-info">
              <i className="fas fa-building me-1"></i>
              {item.organization.name || 'Org-Specific'}
            </span>
          ) : (
            <span className="badge bg-secondary">
              <i className="fas fa-globe me-1"></i>
              Global
            </span>
          )}
        </td>
        <td>{item.order}</td>
        {activeTab === 'types' && (
          <td>
            {item.icon && <i className={`fas fa-${item.icon} me-2`}></i>}
            {item.icon}
          </td>
        )}
        {activeTab === 'statuses' && (
          <td>
            <span
              className="badge"
              style={{ backgroundColor: item.color, color: '#fff' }}
            >
              {item.color}
            </span>
          </td>
        )}
        {activeTab === 'statuses' && (
          <td>
            {item.is_default ? (
              <span className="badge bg-warning text-dark">
                <i className="fas fa-star me-1"></i>Default
              </span>
            ) : (
              <button
                className="btn btn-sm btn-outline-warning"
                title="Set as default"
                onClick={() => handleSetDefault(item)}
              >
                <i className="fas fa-star"></i>
              </button>
            )}
          </td>
        )}
        {activeTab === 'statuses' && (
          <td>
            {item.is_complete ? (
              <span className="badge bg-success">
                <i className="fas fa-check-circle me-1"></i>Yes
              </span>
            ) : (
              <span className="badge bg-secondary">No</span>
            )}
          </td>
        )}
        <td>
          {item.is_active ? (
            <span className="badge bg-success">Active</span>
          ) : (
            <span className="badge bg-secondary">Inactive</span>
          )}
        </td>
        <td>
          <button
            className="btn btn-sm btn-outline-primary me-1"
            onClick={() => handleEdit(item)}
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(item)}
          >
            <i className="fas fa-trash"></i>
          </button>
        </td>
      </tr>
    ));

    const tableHeader = (
      <thead>
        <tr>
          <th>Name</th>
          <th>Slug</th>
          <th>Scope</th>
          <th>Order</th>
          {activeTab === 'types' && <th>Icon</th>}
          {activeTab === 'statuses' && <th>Color</th>}
          {activeTab === 'statuses' && <th>Default</th>}
          {activeTab === 'statuses' && <th>Complete</th>}
          <th>Active</th>
          <th>Actions</th>
        </tr>
      </thead>
    );

    return (
      <div>
        {/* Scoped Items Table */}
        {scopedItems.length > 0 && (
          <div className="mb-4">
            <h6 className="text-primary mb-2">
              <i className="fas fa-filter me-2"></i>
              {filterType === 'health_system' ? 'Health System Specific' : 'Organization Specific'} Items
              <span className="badge bg-primary ms-2">{scopedItems.length}</span>
            </h6>
            <table className="table table-hover">
              {tableHeader}
              <tbody>{renderItemRows(scopedItems)}</tbody>
            </table>
          </div>
        )}

        {/* Global Items Table */}
        {globalItems.length > 0 && (
          <div>
            <h6 className="text-secondary mb-2">
              <i className="fas fa-globe me-2"></i>
              Global Items (Available to All)
              <span className="badge bg-secondary ms-2">{globalItems.length}</span>
            </h6>
            <table className="table table-hover">
              {tableHeader}
              <tbody>{renderItemRows(globalItems)}</tbody>
            </table>
          </div>
        )}

        {/* No items message */}
        {scopedItems.length === 0 && globalItems.length === 0 && (
          <div className="alert alert-info">
            No items found. Click &quot;Create All Defaults&quot; to get started.
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleSave}>
        <div className="mb-3">
          <label className="form-label">Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Slug *</label>
          <input
            type="text"
            className="form-control"
            value={formData.slug || ''}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
            pattern="[a-z0-9-]+"
            title="Only lowercase letters, numbers, and hyphens"
          />
          <small className="form-text text-muted">
            Lowercase letters, numbers, and hyphens only
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="mb-3">
          <label className="form-label d-block">
            <i className="fas fa-building me-2"></i>
            Assignment Type
          </label>

          {/* Radio buttons for form assignment type */}
          <div className="btn-group mb-2" role="group">
            <input
              type="radio"
              className="btn-check"
              name="formAssignmentType"
              id="formAssignmentGlobal"
              checked={formAssignmentType === 'global'}
              onChange={() => handleFormAssignmentTypeChange('global')}
            />
            <label className="btn btn-outline-secondary" htmlFor="formAssignmentGlobal">Global</label>

            <input
              type="radio"
              className="btn-check"
              name="formAssignmentType"
              id="formAssignmentHealthSystem"
              checked={formAssignmentType === 'health_system'}
              onChange={() => handleFormAssignmentTypeChange('health_system')}
            />
            <label className="btn btn-outline-primary" htmlFor="formAssignmentHealthSystem">Health System</label>

            <input
              type="radio"
              className="btn-check"
              name="formAssignmentType"
              id="formAssignmentOrganization"
              checked={formAssignmentType === 'organization'}
              onChange={() => handleFormAssignmentTypeChange('organization')}
            />
            <label className="btn btn-outline-info" htmlFor="formAssignmentOrganization">Organization</label>
          </div>

          {/* Conditional dropdowns */}
          {formAssignmentType === 'health_system' && (
            <div>
              <select
                className="form-select"
                value={formData.health_system_id || ''}
                onChange={(e) => setFormData({ ...formData, health_system_id: e.target.value || null })}
              >
                <option value="">Select a health system</option>
                {healthSystems.map((hs) => (
                  <option key={hs.id} value={hs.id}>{hs.name}</option>
                ))}
              </select>
              <small className="form-text text-muted">
                This item will only be available for the selected health system
              </small>
            </div>
          )}

          {formAssignmentType === 'organization' && (
            <div>
              <select
                className="form-select"
                value={formData.organization_id || ''}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value || null })}
              >
                <option value="">Select an organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <small className="form-text text-muted">
                This item will only be available for the selected organization
              </small>
            </div>
          )}

          {formAssignmentType === 'global' && (
            <small className="form-text text-muted d-block">
              Global items apply to all organizations and health systems
            </small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Display Order</label>
          <input
            type="number"
            className="form-control"
            value={formData.order || 0}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
          />
        </div>

        {activeTab === 'types' && (
          <div className="mb-3">
            <label className="form-label">Icon (FontAwesome)</label>
            <input
              type="text"
              className="form-control"
              value={formData.icon || ''}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="e.g., heartbeat, lungs, brain"
            />
            <small className="form-text text-muted">
              FontAwesome icon name (without fa- prefix)
            </small>
          </div>
        )}

        {activeTab === 'statuses' && (
          <div className="mb-3">
            <label className="form-label">Color</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={formData.color || '#808080'}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            />
          </div>
        )}

        {activeTab === 'statuses' && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isDefault"
              checked={formData.is_default || false}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="isDefault">
              Set as default (initial) status for new referrals
            </label>
            <div className="form-text text-muted">
              Only one status can be the default. Setting this will unset the current default.
            </div>
          </div>
        )}

        {activeTab === 'statuses' && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isComplete"
              checked={formData.is_complete || false}
              onChange={(e) => setFormData({ ...formData, is_complete: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="isComplete">
              <i className="fas fa-check-circle me-1 text-success"></i>
              Completion status (auto-completes items)
            </label>
            <div className="form-text text-muted">
              When an item is set to this status, it will automatically be marked as complete.
            </div>
          </div>
        )}

        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isActive"
            checked={formData.is_active !== false}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="isActive">
            Active
          </label>
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>Save</>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowForm(false);
              setFormData({});
              setEditingItem(null);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="referral-management-settings p-3">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Scope Filter Selector */}
      <div className="mb-3">
        <label className="form-label fw-semibold d-block mb-2">
          <i className="fas fa-filter me-2"></i>
          Filter Scope:
        </label>

        {/* Radio buttons for filter type */}
        <div className="btn-group mb-2" role="group">
          <input
            type="radio"
            className="btn-check"
            name="filterType"
            id="filterGlobal"
            checked={filterType === 'global'}
            onChange={() => handleFilterTypeChange('global')}
          />
          <label className="btn btn-outline-secondary" htmlFor="filterGlobal">Global</label>

          <input
            type="radio"
            className="btn-check"
            name="filterType"
            id="filterHealthSystem"
            checked={filterType === 'health_system'}
            onChange={() => handleFilterTypeChange('health_system')}
          />
          <label className="btn btn-outline-primary" htmlFor="filterHealthSystem">Health System</label>

          <input
            type="radio"
            className="btn-check"
            name="filterType"
            id="filterOrganization"
            checked={filterType === 'organization'}
            onChange={() => handleFilterTypeChange('organization')}
          />
          <label className="btn btn-outline-info" htmlFor="filterOrganization">Organization</label>
        </div>

        {/* Conditional dropdowns based on filter type */}
        {filterType === 'health_system' && (
          <div>
            <select
              className="form-select"
              style={{ maxWidth: '400px' }}
              value={selectedHealthSystemId}
              onChange={(e) => setSelectedHealthSystemId(e.target.value)}
            >
              <option value="">Select a health system</option>
              {healthSystems.map((hs) => (
                <option key={hs.id} value={hs.id}>{hs.name}</option>
              ))}
            </select>
            <small className="text-muted d-block mt-1">
              Showing items scoped to the selected health system
            </small>
          </div>
        )}

        {filterType === 'organization' && (
          <div>
            <select
              className="form-select"
              style={{ maxWidth: '400px' }}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <option value="">Select an organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <small className="text-muted d-block mt-1">
              Showing items scoped to the selected organization
            </small>
          </div>
        )}

        {filterType === 'global' && (
          <small className="text-muted d-block">
            Showing global items that apply to all organizations and health systems
          </small>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('types');
              setShowForm(false);
            }}
          >
            Referral Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'statuses' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('statuses');
              setShowForm(false);
            }}
          >
            Referral Statuses
          </button>
        </li>
      </ul>

      {/* Action Bar */}
      {!showForm && (
        <div className="d-flex justify-content-end me-2 mb-3">
          <button className="btn btn-primary btn-sm" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            Add New
          </button>
        </div>
      )}

      {/* Content */}
      {showForm ? (
        <div className="card">
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingItem ? 'Edit' : 'Create New'}{' '}
              {activeTab === 'types' && 'Referral Type'}
              {activeTab === 'statuses' && 'Referral Status'}
            </h6>
            {renderForm()}
          </div>
        </div>
      ) : (
        <div className="card border-0 box-shadow-0">
          {renderTable()}
        </div>
      )}
    </div>
  );
};

export default ReferralManagementSettings;
